<template>

  <!-- XOL-67 (B6): the el-form and tabs of SiteAction.vue (Sites -> Add / Edit), extracted so that Step 5 of an RTA can
       render the same form in `capture` mode. SiteAction keeps its Popup and its save; this component owns nothing but the
       form: it edits the object it is given (`value`: { site, struct, finance, tenancy, candidate, loc }) in place, as the
       template always did, and says `input` whenever anything in it changed. -->
  <el-form ref="siteForm" :model="site" :rules="rules" label-position="top" inline :class="{ 'site-form--readonly': capture && readOnly }">

      <el-row type="flex" justify="center">
        <el-col :span="24" align="center">
          <h4>Site Name: <span :class="titleClass(site.status)">{{site.siteName ? site.siteName : 'not entered'}}</span></h4>
        </el-col>
      </el-row>

      <el-tabs v-model="tab" type="border-card">

        <el-tab-pane name="site">
          <span slot="label"><i v-if="tabErrors.site" class="fa fa-exclamation-circle text-danger site-form__tab-error" aria-hidden="true"></i>Details<span v-if="tabErrors.site" class="site-form__sr"> (has errors)</span></span>

          <div v-if="site.status === this.soldSite()">
            <el-alert type="error" :closable="false" show-icon title="Sold Site Warning!" description="If you save, ALL sharers will be set to 'Terminated'! Plus you won't be able to edit site details anymore." />
          </div>

          <!-- Capture mode (an RTA's new site): the status is not a choice. An RTA always creates a Candidate site (decision 78), which a
               nightly job moves to its handover status once the handover date has arrived (decisions 83, 85). -->
          <el-form-item v-if="capture" key="status-fixed" label="Status">
            <!-- Decision 113 puts the CURRENT status on the left: Candidate for a new site, the kept site's own status under decision 108 (decision 119). -->
            <b class="site-form__status">{{ transitionedStatus || 'Candidate' }}</b>
            <div v-if="transitionedStatus"><small class="site-form__status-note">{{ siteLabel }} is already {{ transitionedStatus }}. Completing this RTA keeps its status and handover date as they are.</small></div>
            <div v-else><small class="site-form__status-note">{{ statusNote }}</small></div>
          </el-form-item>

          <!-- The key changes when the cue appears or goes: a forwarded slot of a select that is already mounted did not repaint (the note never
               showed), and a new instance also reads the new status as its label. -->
          <select-site-status
            v-else
            :key="statusCue ? 'status-select-cue' : 'status-select'"
            ref="selectSite"
            label="Status"
            v-model="site.status"
            :saved-value="originals && originals.site ? originals.site.status : undefined"
            required
            :disabled="this.isDisabled(originals.site, site, 'status')">
            <!-- A Candidate site cannot be set back to Candidate (API use_rta), and leaving Candidate cancels its scheduled change (decision 84). -->
            <template #cue><small v-if="statusCue" class="site-form__note">{{ statusCue }}</small></template>
          </select-site-status>

          <!-- Decision 113: ONE row. The current status (left), an arrow (no caption: the group carries it), then the Handover group holding the date and the
               status the site becomes. Step 5 always draws it; a Candidate site's Site Details draws it while the form's status is still
               Candidate. Block break so the row stands on its own line in this inline form. -->
          <template v-if="showHandover">
            <div key="handover-arrow" class="site-form__handover-arrow" aria-hidden="true">
              <i class="fa fa-arrow-right site-form__handover-arrow-right"></i>
              <i class="fa fa-arrow-down site-form__handover-arrow-down"></i>
            </div>

            <!-- tabindex -1: a failed Save focuses the GROUP (focusHandover), not the date input, which would open Element's calendar over the message.
                 The two fields below have no `prop`: a form item with a `prop` validates on blur (Element), which wiped the message of "A handover
                 status needs a handover date." and, with `required`, replaced "Enter the handover date." with async-validator's own text. These
                 fields are checked at Save / Complete only, and their messages come in through `error`. -->
            <div key="handover-group" ref="handoverGroup" class="site-form__handover-group" role="group" aria-label="Handover" tabindex="-1" :aria-describedby="handoverError ? handoverErrorId : null">
              <small class="site-form__handover-caption">Handover</small>
              <!-- Element's own message under the date has no id and no role, so nothing says it aloud: this copy is what the group (and the date input)
                   point at, only while there is a message. -->
              <span v-if="handoverError" :id="handoverErrorId" class="site-form__sr">{{ handoverError }}</span>

              <!-- Decision 108 (Step 5 of a legacy RTA whose kept site has already left Candidate): its current handover date and status, as values. -->
              <template v-if="fixedHandover">
                <date-input
                  v-if="transitionedHandover"
                  key="handover-date-fixed"
                  :value="transitionedHandover"
                  label="Date"
                  aria-label="Handover date"
                  :disabled="true" />
                <el-form-item v-else key="handover-date-empty" label="Date"><b>Not entered</b></el-form-item>

                <el-form-item key="handover-status-fixed" label="Status"><b>{{ transitionedStatus }}</b></el-form-item>
              </template>

              <template v-else>
                <date-input
                  key="handover-date"
                  v-model="site.handover"
                  label="Date"
                  aria-label="Handover date"
                  :invalid="!!errors['site.handover']"
                  :described-by="handoverErrorId"
                  placeholder="Not scheduled"
                  :error="errors['site.handover'] || null"
                  :disabled="this.isDisabled(originals.site, site, 'handover')" />

                <select-site-status
                  key="handover-status"
                  v-model="site.handoverStatus"
                  label="Status"
                  aria-label="Handover status"
                  placeholder="Not scheduled"
                  :only="handoverStatuses"
                  :clearable="true"
                  :error="errors['site.handoverStatus'] || null"
                  :disabled="this.isDisabled(originals.site, site, 'handoverStatus')">
                  <!-- Step 5 (UX spec 2.8): where a pre-filled status came from, while it still stands. -->
                  <template #cue><small v-if="capture && handoverStatusNote" class="site-form__note">{{ handoverStatusNote }}</small></template>
                </select-site-status>
              </template>
            </div>

            <div class="site-form__break"></div>

            <!-- Decision 86: a date of today or earlier only warns; the nightly run, never the save, moves the site. A block after the group,
                 not in DateInput's cue slot: this form is inline (XOL-416). A stable key so the alert is not announced again while typing. -->
            <el-alert
              v-if="handoverWarn"
              key="handover-warning"
              class="site-form__handover-warning"
              type="warning"
              show-icon
              :closable="false"
              :title="handoverWarn.title"
              :description="handoverWarn.description" />
          </template>


          <text-input
            label="Site Name"
            prop="siteName"
            required
            v-model="site.siteName"
            placeholder="Site Name"
            :error="errors['site.siteName'] || null"
            :error-id="capture ? 'site-form-siteName-error' : null"
            :validate-event="!capture"
            :disabled="isIdentityDisabled('siteName')">
            <template #cue><small v-if="capture && siteNameNote" class="site-form__note site-form__note--field">{{ siteNameNote }}</small></template>
          </text-input>

          <text-input
            label="Site ID"
            prop="siteId"
            required
            v-model="site.siteId"
            placeholder="Site ID"
            :max-length="16"
            :error="errors['site.siteId'] || null"
            :error-id="capture ? 'site-form-siteId-error' : null"
            :validate-event="!capture"
            :disabled="isIdentityDisabled('siteId')">
            <template #cue><small v-if="capture && siteIdNote" class="site-form__note site-form__note--field">{{ siteIdNote }}</small></template>
          </text-input>

          <text-input
              label="Site Group"
              prop="siteGroup"
              v-model="site.siteGroup"
              placeholder="Site Group"
              :max-length="16"
              :error="errors['site.siteGroup'] || null"
              :disabled="this.isDisabled(originals.site, site, 'siteGroup')" />

        <select-coverage
            v-model="site.coverage"
            label="Coverage"
            :error="errors['site.coverage'] || null"
            :disabled="this.isDisabled(originals.site, site, 'coverage')" />

          <date-input
            v-if="!showHandover"
            key="handover-date-plain"
            v-model="site.handover"
            label="Handover"
            prop="handover"
            :error="errors['site.handover'] || null"
            :disabled="this.isDisabled(originals.site, site, 'handover')" />

          <date-input
            v-if="site.status === this.activeSite()"
            v-model='site.active'
            label="Active"
            prop="active"
            :error="errors['site.active'] || null"
            :disabled="this.isDisabled(originals.site, site, 'active')" />

          <date-input
              v-if="site.status === this.dismantledSite()"
              v-model='site.dismantled'
              label="Dismantled"
              prop="dismantled"
              :disabled="this.isDisabled(originals.site, site, 'dismantled')" />


          <el-row>
            <el-col :span="24" align="center">
              <check-box v-if="!isAdd && isManager('project|management') && !isViewOnly()" v-model="overrideModel"><b>Manager Only!</b> Change existing data?</check-box>
            </el-col>
         </el-row>

        </el-tab-pane>


        <el-tab-pane label="Candidate" v-if="!capture && isAdd && this.isCandidate(site.status)">
          <el-row>
            <el-col :span="24">
              <select-sharer v-model="candidate.client_id" />

              <text-input
                   label="Your Ref"
                   v-model="candidate.clientRef"
                   placeholder="Your Ref"
                   :max-length="16"
                    />

              <text-input
                   label="Title"
                   v-model="candidate.title"
                   placeholder="Title"
                   :max-length="88"
                    />

               <select-coverage
                   v-model="candidate.stationType"
                   label="Station Type"
                    />

              <!-- Decision 185: empty or a whole 0 to 121 (0 is a value; a cleared field is empty, not 0). -->
              <integer-input
                  label="Antenna AGL"
                  :min="0"
                  :max="121"
                  suffix="m."
                  placeholder="Height above ground in metres"
                  empty-is-null
                  v-model="candidate.antennaHeight"
                  />


            </el-col>
          </el-row>

        </el-tab-pane>

        <el-tab-pane v-if="isAdd && !isLandOnly(site.status) && !landOnlyHandover && struct" name="structure">
          <span slot="label"><i v-if="tabErrors.structure" class="fa fa-exclamation-circle text-danger site-form__tab-error" aria-hidden="true"></i>Structure<span v-if="tabErrors.structure" class="site-form__sr"> (has errors)</span></span>

          <el-row :gutter="guts">
            <el-col :span="24">

              <select-structure
                label="Structure Type"
                v-model="struct.structure_id"
                :disabled-text="capture ? structureText : null"
                :disabled="isDisabled(originals.struct, struct, 'structure_id')" />

              <select-tower-supplier
                v-model="struct.supplier_id"
                :full-object="false"
                :disabled="isDisabled(originals.struct, struct, 'supplier_id')" />

            </el-col>
          </el-row>

          <el-row :gutter="guts">
              <el-col :span="24">
                <integer-input
                  label="Structure Height"
                  :min="1"
                  :max="121"
                  suffix="m."
                  v-model="struct.height"
                  :disabled="isDisabled(originals.struct, struct, 'height')" />

              <integer-input
                label="Design Weight"
                :min="1"
                :max="121"
                suffix="kgs."
                placeholder="weight in kgs."
                v-model="struct.weight"
                :disabled="isDisabled(originals.struct, struct, 'weight')" />

              </el-col>
          </el-row>

          <el-row :gutter="guts">
            <el-col :span="24">

              <integer-input
                  label="Site Width"
                  v-model="struct.width"
                  suffix="m."
                  placeholder="width in metres"
                  :disabled="this.isDisabled(originals.struct, struct, 'width')" />

              <integer-input
                  label="Site Depth"
                  v-model="struct.depth"
                  suffix="m."
                  placeholder="depth in metres"
                  :disabled="this.isDisabled(originals.struct, struct, 'depth')" />

            </el-col>
          </el-row>

        </el-tab-pane>



        <el-tab-pane name="locality">
          <span slot="label"><i v-if="tabErrors.locality" class="fa fa-exclamation-circle text-danger site-form__tab-error" aria-hidden="true"></i>Locality<span v-if="tabErrors.locality" class="site-form__sr"> (has errors)</span></span>

          <el-row :gutter="guts">
            <el-col :xs="24" :sm="24" :md="12" :lg="8">
              <text-input
                style="width: 100%; height: 100px;"
                label="Address"
                type="textarea"
                v-model="site.address"
                placeholder="Site Address"
                :error="errors['site.address'] || null"
                :disabled="this.isDisabled(originals.site, site, 'address')" />
            </el-col>

            <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <text-input
                  label="Post Code"
                  prop="postCode"
                  v-model="site.postCode"
                  placeholder="Post Code"
                  :error="errors['site.postCode'] || null"
                  :disabled="this.isDisabled(originals.site, site, 'postCode')" />
              </el-col>

              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                  <select-locality
                    label="District"
                    v-model="loc"
                    :disabled="isDisabled(originals.site,
                      site,
                      ['zone','subdistrict', 'district', 'state', 'region', 'country'])"  />
              </el-col>

          </el-row>

          <hr />

          <el-row :gutter="guts">
            <el-col :span="8" align="center">

              <float-input
                v-if="!movePos"
                type="number"
                :decimals="12"
                v-model="site.latitude"
                placeholder="Latitude"
                prefix="Lat"
                suffix="&deg;"
                :disabled="this.isDisabled(originals.site, site, 'latitude')" />
                <span v-else>Latitude:&nbsp;<b>{{site.latitude}}</b>&deg;</span>

            </el-col>

            <el-col :span="8" align="center">

              <float-input
                v-if="!movePos"
                :decimals="12"
                type="number"
                v-model="site.longitude"
                placeholder="Longitude"
                prefix="Lng" suffix="&deg;"
                :disabled="this.isDisabled(originals.site, site, 'longitude')" />
                <span v-else>Longitude:&nbsp;<b>{{site.longitude}}</b>&deg;</span>

            </el-col>

            <el-col :span="8" align="center">

              <float-input
                type="number"
                :decimals="2"
                v-model="site.agl"
                placeholder="AGL"
                prefix="AGL"
                suffix="&deg;"
                :disabled="this.isDisabled(originals.site, site, 'latitude')" />

            </el-col>


          </el-row>

          <google-map
            v-if="this.site.latitude && this.site.longitude"
            :lat="this.site.latitude"
            :lng="this.site.longitude"
            :draggable="!(capture && readOnly)"
            :zoom="14"
            width="100%"
            height="41.7vh"
            @update="updateSiteMap" />


        </el-tab-pane>


        <el-tab-pane v-if="isAdd" name="tenancy">
          <span slot="label"><i v-if="tabErrors.tenancy" class="fa fa-exclamation-circle text-danger site-form__tab-error" aria-hidden="true"></i>Tenancy<span v-if="tabErrors.tenancy" class="site-form__sr"> (has errors)</span></span>

          <!-- Read-only (an RTA seen by a Tower Co user who may not fill it in): UpdateTenancy has many controls its own `disabled`
               does not reach, so the pane takes no pointer and its controls leave the tab order. It stays in the accessibility tree
               (no `inert`, no aria-hidden): a screen reader still reads what the site will be created with. -->
          <div ref="tenancyPane" :class="{ 'site-form__locked': capture && readOnly }">
            <update-tenancy
              :site-data="site"
              :tenancy-data="tenancy"
              adding
              editable
              no-disable
              @update="updateTenancy" />
          </div>
        </el-tab-pane>



        <el-tab-pane v-if="isAdd && !isLandOnly(site.status) && !landOnlyHandover && finance" name="finance">
          <span slot="label"><i v-if="tabErrors.finance" class="fa fa-exclamation-circle text-danger site-form__tab-error" aria-hidden="true"></i>Finance<span v-if="tabErrors.finance" class="site-form__sr"> (has errors)</span></span>

            <number-input
              v-model="finance.capex"
              label="Site CAPEX"
              prop="capex"
              placeholder="Site CAPEX"
              :disabled="this.isDisabled(originals.finance, finance, 'capex')" />

            <number-input
              v-model="finance.opex"
              label="Site OPEX"
              prop="opex"
              placeholder="Site OPEX"
              :disabled="this.isDisabled(originals.finance, finance, 'opex')" />

        </el-tab-pane>

      </el-tabs>


</el-form>

</template>

<script>

/*
 * XOL-67 (B6): the form of SiteAction.vue, extracted. Two callers:
 *
 *   - SiteAction (Sites -> Add / Edit): `adding` is Add vs Edit, `originals` feeds the field-by-field lock of an edit
 *     (`isDisabled`), `override` is the manager's "Change existing data?" box. Behaviour is the one it had inside SiteAction.
 *   - RtaCompletion (Step 5): `capture`. The status is a bold "Active" with no select, there is no Candidate tab and no
 *     override box, UpdateTenancy runs as in Add (`no-disable`), and every field takes `:disabled="readOnly"` instead of
 *     the field lock. The values are edited in place and handed up through `input` / v-model; nothing is POSTed here.
 *
 * `value` is { site, struct, finance, tenancy, candidate, loc }, owned by the parent and edited in place (the template always
 * wrote straight into these objects). `input` is emitted with the same object after ANY change in it (a deep watch), so a
 * parent that wants "the user edited something" listens to it; a change the parent made itself comes back through it too.
 */

import SiteMixins from '@/mixins/SiteMixins'
import { structurePath, handoverWarning, handoverWarningCopy } from '@/lib/rtaCompletion'
import { appNowDateSql } from '@/lib/appTime'
import { ymd } from '@/lib/siteTransition'
import { HANDOVER_TRANSITION_STATUSES } from '@/components/Data'

import SelectCoverage from '@/components/atoms/selects/SelectCoverage'
import SelectLocality from '@/components/atoms/selects/SelectLocality'

import SelectSiteStatus from '@/components/atoms/selects/SelectSiteStatus'
import SelectSharer from '@/components/atoms/selects/SelectSharer'
import SelectStructure from '@/components/atoms/selects/SelectStructure'

import SelectTowerSupplier from '@/components/atoms/selects/SelectTowerSupplier'

import UpdateTenancy from '@/components/property/UpdateTenancy'

import GoogleMap from '@/components/atoms/GoogleMap'

export default {
  name: 'SiteForm',

  mixins: [SiteMixins],

  components: {
    GoogleMap,
    SelectCoverage,
    SelectLocality,
    SelectSharer,
    SelectStructure,
    SelectSiteStatus,
    SelectTowerSupplier,
    UpdateTenancy
  },

  props: {

    // { site, struct, finance, tenancy, candidate, loc }
    value: {
      type: Object,
      required: true
    },

    // Sites -> Add (SiteAction's addSite). Capture mode is always an add.
    adding: {
      type: Boolean,
      default: false
    },

    // The saved records an Edit compares against (`isDisabled`): { site, struct, finance }.
    originals: {
      type: Object,
      default: function () { return { site: {}, struct: {}, finance: {} } }
    },

    // The manager's "Change existing data?" box (.sync).
    override: {
      type: Boolean,
      default: false
    },

    // Step 5 of an RTA: see the header.
    capture: {
      type: Boolean,
      default: false
    },

    // Capture mode only: show the values, change nothing.
    readOnly: {
      type: Boolean,
      default: false
    },

    // Capture mode: inline errors by 'section.field' ('site.siteName': 'Enter the Site Name.'), and the tabs that hold one.
    errors: {
      type: Object,
      default: function () { return {} }
    },

    tabErrors: {
      type: Object,
      default: function () { return {} }
    },

    // The selected tab (.sync), so a parent can open the first tab with an error.
    activeTab: {
      type: String,
      default: null
    },

    // XOL-67 (B8, decisions 84, 104): this is a Candidate site's Site Details (SiteAction computes it: an edit of ANY Candidate site, the
    // legacy site with an open RTA included). While the form's status is still Candidate it draws the handover row (the date and the
    // status the site becomes) and exempts the two fields from the "already has a value" lock. Capture mode (Step 5) draws the row always.
    handoverTransition: {
      type: Boolean,
      default: false
    },

    // Capture mode, decision 108: the kept legacy site has already left Candidate, so the two handover fields show its CURRENT status and
    // handover date as values (never written to the overlay) and are not required. `transitionedHandover` is its date.
    transitionedStatus: {
      type: String,
      default: null
    },

    transitionedHandover: {
      type: String,
      default: null
    },

    // Capture mode (XOL-67 V6, decisions 158 Q3, 161): Site Name and Site ID are a Tower Co manager's. True shows them as values for anyone else,
    // whatever `readOnly` says about the other fields.
    identityReadOnly: {
      type: Boolean,
      default: false
    },

    // Capture mode, UX spec 2.8: the small line under a pre-filled field ("From the request.", "From option 2.", "From the legacy site record."). '' draws none.
    siteNameNote: {
      type: String,
      default: ''
    },

    siteIdNote: {
      type: String,
      default: ''
    },

    handoverStatusNote: {
      type: String,
      default: ''
    }

  },


  computed: {

    site () { return this.value.site },
    struct () { return this.value.struct },
    finance () { return this.value.finance },
    tenancy () { return this.value.tenancy },
    candidate () { return this.value.candidate },

    loc: {
      get () { return this.value.loc },
      set (loc) { this.value.loc = loc }
    },

    overrideModel: {
      get () { return this.override },
      set (on) { this.$emit('update:override', on) }
    },

    // The Tenancy, Structure and Finance tabs are the add form's; Step 5 is an add too.
    isAdd () {
      return this.adding || this.capture
    },

    // The handover row (decision 113): Step 5 always; a Candidate site's edit while the form's status is still Candidate.
    showHandover () {
      return this.capture || (this.handoverTransition && this.isCandidate(this.site.status))
    },

    // The message on the handover date (Step 5's precheck, or the edit's before-save), and the id the group and the date input describe themselves by.
    handoverError () {
      // The date's message, else the status's (the Step 5 pair rule is keyed on the status, G3). The date input's own invalid stays tied to the date.
      if (!this.errors) {
        return ''
      }

      return this.errors['site.handover'] || this.errors['site.handoverStatus'] || ''
    },

    handoverErrorId () {
      return 'site-form-handover-error-' + this._uid
    },

    // Decision 194: the note under the bold Candidate follows what is entered. Neither field is required at Step 5.
    statusNote () {
      var prefix = 'An RTA always creates a Candidate site. '
      var date = ymd(this.site.handover) !== ''
      var status = this.site.handoverStatus

      if (date && status) {
        return prefix + 'On its handover date it changes to ' + status + '.'
      }

      if (status) {
        return prefix + 'It stays Candidate until a handover date is set, then changes to ' + status + '.'
      }

      if (date) {
        return prefix + 'Select the status it changes to on its handover date.'
      }

      return prefix + 'With no handover date, it stays Candidate until one is set.'
    },

    // Decision 108: the fields are values.
    fixedHandover () {
      return this.capture && !!this.transitionedStatus
    },

    // The four statuses a site can be scheduled to become, in the config's order (SelectSiteStatus `only`).
    handoverStatuses () {
      return HANDOVER_TRANSITION_STATUSES
    },

    // Decision 86: today or earlier, with a status chosen. The dates are compared as 'YYYY-MM-DD' strings (XOL-414).
    handoverWarn () {
      if (!this.showHandover || this.fixedHandover) {
        return null
      }

      return handoverWarning(this.formatDateSql(this.site.handover), this.site.handoverStatus, appNowDateSql())
        ? handoverWarningCopy(this.site.siteName, this.site.handoverStatus)
        : null
    },

    // UX decision 117 (3): the API skips Structure and Finance for a Land-Only handover (DEV-37), so Step 5 hides their tabs (the overlay keeps the values).
    landOnlyHandover () {
      return this.capture && !this.fixedHandover && this.site.handoverStatus === this.landOnlySite()
    },

    siteLabel () {
      return this.site.siteName ? this.site.siteName : 'The site'
    },

    // A Candidate site's edit, after its status has left Candidate: what saving does to the scheduled change.
    statusCue () {
      if (this.capture || !this.handoverTransition || !this.site.status || this.isCandidate(this.site.status)) {
        return ''
      }

      var was = this.originals && this.originals.site ? this.originals.site.handoverStatus : null

      return 'A site can\'t be set back to Candidate after you save.' + (was ? ' Its scheduled change to ' + was + ' is cancelled.' : '')
    },

    // Capture mode is pre-filled, and SelectStructure shows a value only through `disabled-text` (read once, when it is created, which
    // is why Step 5 builds a new form for each set of values). Sites -> Add starts empty and passes nothing.
    structureText () {
      return structurePath(this.getConfig().structures, this.struct.structure_id)
    }

  },


  methods: {

    updateTenancy (tenancy) {
      this.value.tenancy = tenancy
      this.value.site.tenancy = tenancy
    },

    updateSiteMap (pos) {
      this.movePos = true
      this.value.site.latitude = pos.lat
      this.value.site.longitude = pos.lng
    },

    // Read only: the Tenancy pane's controls leave the tab order (the pane also takes no pointer). UpdateTenancy draws its inner tabs and
    // pickers as it goes, so what appears later is locked as it appears.
    lockTenancyPane () {
      var pane = this.$refs.tenancyPane

      if (!pane || typeof pane.querySelectorAll !== 'function') {
        return
      }

      var nodes = pane.querySelectorAll('input, select, textarea, button, a[href], [tabindex]')

      for (var i = 0; i < nodes.length; i++) {
        if (!nodes[i].hasAttribute('data-site-form-locked')) {
          nodes[i].setAttribute('data-site-form-locked', nodes[i].getAttribute('tabindex') === null ? '' : nodes[i].getAttribute('tabindex'))
          nodes[i].setAttribute('tabindex', '-1')
        }
      }
    },

    unlockTenancyPane () {
      var pane = this.$refs.tenancyPane

      if (!pane || typeof pane.querySelectorAll !== 'function') {
        return
      }

      var nodes = pane.querySelectorAll('[data-site-form-locked]')

      for (var i = 0; i < nodes.length; i++) {
        var was = nodes[i].getAttribute('data-site-form-locked')

        if (was === '') {
          nodes[i].removeAttribute('tabindex')
        } else {
          nodes[i].setAttribute('tabindex', was)
        }

        nodes[i].removeAttribute('data-site-form-locked')
      }
    },

    watchTenancyPane () {
      var self = this

      this.stopWatchingTenancyPane()

      if (!(this.capture && this.readOnly) || !this.$refs.tenancyPane) {
        this.unlockTenancyPane()
        return
      }

      this.lockTenancyPane()

      if (typeof MutationObserver !== 'undefined') {
        this.paneObserver = new MutationObserver(function () { self.lockTenancyPane() })
        this.paneObserver.observe(this.$refs.tenancyPane, { childList: true, subtree: true })
      }
    },

    stopWatchingTenancyPane () {
      if (this.paneObserver) {
        this.paneObserver.disconnect()
      }

      this.paneObserver = null
    },

    // Element's validate(), as a Promise<boolean>.
    validate () {
      var form = this.$refs.siteForm

      return new Promise(function (resolve) {
        if (!form || typeof form.validate !== 'function') {
          resolve(true)
          return
        }

        form.validate(function (ok) { resolve(!!ok) })
      })
    },

    // XOL-67 (B8): the Save of a Candidate site's edit failed the handover check; the Handover group (where the message is) is where to look.
    // The group is focused, not the date input: Element opens the calendar when the input is focused, over the message.
    focusHandover () {
      var self = this

      this.tab = 'site'

      this.$nextTick(function () {
        var group = self.$refs.handoverGroup

        if (group && typeof group.focus === 'function') {
          group.focus()
        }
      })
    },

    // Site Name and Site ID: the form's own rule, and in Step 5 a Tower Co manager's alone (`identityReadOnly`).
    isIdentityDisabled (prop) {
      return this.isDisabled(this.originals.site, this.site, prop) || (this.capture && this.identityReadOnly)
    },

    isDisabled (source, target, prop) {

      if (this.capture) {
        return this.readOnly
      }

      // Decision 182 (Carlos, 2026-10-29; replaces 84, which kept them always editable): editing a Candidate site, the handover date and status are
      // locked until "Manager Only! Change existing data?" is ticked, whether or not they hold a value. Adding a site is unchanged.
      if (this.handoverTransition && (prop === 'handover' || prop === 'handoverStatus')) {
        return !(this.isAdd || this.override)
      }

      if (this.isAdd || this.override) {
        return false
      } else {

        if (prop && source && source.hasOwnProperty(prop) && source[prop] !== null) {
          return true
        } else {
          return false
        }
      }
    }

  },


  watch: {

    // Say that something in the form changed (the object is edited in place).
    value: {
      deep: true,
      handler () {
        this.$emit('input', this.value)
      }
    },

    readOnly () {
      this.$nextTick(this.watchTenancyPane)
    },

    activeTab (name) {
      if (name && name !== this.tab) {
        this.tab = name
      }
    },

    tab (name) {
      this.$emit('update:activeTab', name)
    }

  },


  created () {
    // `handoverStatus` is not a key the sites struct or the defaults carry: it must exist before the form is built or Vue cannot see it
    // change (nothing would save, and the warning would not follow it).
    if ((this.capture || this.handoverTransition) && this.value.site && !('handoverStatus' in this.value.site)) {
      this.$set(this.value.site, 'handoverStatus', null)
    }
  },


  mounted () {
    this.watchTenancyPane()
  },


  beforeDestroy () {
    this.stopWatchingTenancyPane()
  },


  data () {

    return {
      guts: 12,
      movePos: false,
      tab: this.activeTab || 'site',
      rules: {
        siteName: [
          { required: true, message: 'Please enter site name', trigger: 'blur' }
        ],
        status: [
          { required: true, message: 'Please enter site status', trigger: 'blur' }
        ],
        siteId: [
          { required: true, message: 'Please enter site ID', trigger: 'change' }
        ]
      }
    }

  }

}

</script>


<style scoped>
  /* Capture mode, read only: the disabled controls read as values (the text inputs and dates already render bold). */
  .site-form--readonly >>> .el-input.is-disabled .el-input__inner,
  .site-form--readonly >>> .el-textarea.is-disabled .el-textarea__inner {
    background: transparent;
    border-color: transparent;
    color: #303133;
    font-weight: bold;
    cursor: default;
    padding-left: 0;
  }
  .site-form--readonly >>> .el-input__suffix,
  .site-form--readonly >>> .el-input-group__prepend,
  .site-form--readonly >>> .el-input-group__append { display: none; }
  .site-form__locked { pointer-events: none; }
  .site-form__tab-error { margin-right: 4px; }
  .site-form__sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  .site-form__status { font-size: 1.05em; }
  .site-form__status-note { display: block; max-width: 260px; line-height: 1.4; color: #606266; }

  /* Decision 113: the current status, the arrow (no caption, the group carries it), and the bordered Handover group, on one row. */
  .site-form__handover-arrow {
    display: inline-block;
    vertical-align: top;
    margin: 0 12px 0 4px;
    padding-top: 30px;
    text-align: center;
    color: #606266;
  }
  .site-form__handover-arrow-down { display: none; }
  .site-form__handover-group {
    display: inline-block;
    vertical-align: top;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 8px 12px;
  }
  .site-form__handover-group:focus { outline: none; }
  .site-form__handover-group:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; }
  .site-form__handover-caption { display: block; color: #606266; margin-bottom: 4px; }
  .site-form__handover-group >>> .el-form-item { margin-bottom: 4px; }
  /* A message inside the box (not absolutely positioned under it, over the border): the box grows only when there is one. */
  .site-form__handover-group >>> .el-form-item__error { position: static; padding-top: 4px; }
  .site-form__break { display: block; width: 100%; height: 0; }
  .site-form__handover-warning { max-width: 720px; margin: 4px 0 12px; }

  /* The status cue under a Candidate site's status select (decision 99). */
  .site-form__note { display: block; max-width: 220px; font-size: 12px; line-height: 1.4; color: #606266; margin-top: 4px; }
  /* Under a text input of Step 5 (it is block inside Element's inline form item, so it takes the item's width). */
  .site-form__note--field { margin-top: 2px; }

  @media (max-width: 575px) {
    /* The arrow turns down and the Handover group stacks under the current status. */
    .site-form__handover-arrow { display: block; margin: 0 0 4px; padding-top: 0; }
    .site-form__handover-arrow-right { display: none; }
    .site-form__handover-arrow-down { display: inline; }
    .site-form__handover-group { display: block; }
    .site-form__handover-warning { max-width: 100%; }
    .site-form__note { max-width: 100%; }
  }
</style>
